// =============================================================================
// components/AppleSignInButton.tsx — Apple-HIG-compliant custom "Sign in with
// Apple" button.
//
// WHY CUSTOM (not @invertase/react-native-apple-authentication's <AppleButton>):
// that native button is a LEGACY native view that mis-lays-out under bridgeless
// New Architecture (Fabric) — the JS shadow node reserves the correct slot in the
// form, but the underlying UIKit view renders at the window origin, floating over
// the Dynamic Island/status bar. A plain Pressable styled per Apple's Human
// Interface Guidelines (black fill, white Apple logo + label, rounded corners)
// lays out correctly and drives the SAME performAppleLogin() flow via onPress.
// =============================================================================
import React from 'react';
import { Pressable, Text, View, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from './ui/Icon';
import { radius, fonts } from '../constants/theme';

interface Props {
  /** Apple HIG label, e.g. "Sign in with Apple" or "Sign up with Apple". */
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export default function AppleSignInButton({ label, onPress, loading = false, disabled = false }: Props) {
  const inactive = disabled || loading;
  return (
    <Pressable
      onPress={onPress}
      disabled={inactive}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: inactive, busy: loading }}
      style={({ pressed }) => [styles.button, (pressed || inactive) && styles.pressed]}
    >
      {loading ? (
        <ActivityIndicator color="#FFFFFF" size="small" />
      ) : (
        <View style={styles.content}>
          <Ionicons name="logo-apple" size={19} color="#FFFFFF" style={styles.logo} />
          <Text style={styles.label} allowFontScaling={false} numberOfLines={1}>
            {label}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    height: 48,
    width: '100%',
    backgroundColor: '#000000',
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  pressed: { opacity: 0.85 },
  content: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  logo: { marginRight: 8, marginTop: -2 },
  label: { color: '#FFFFFF', fontSize: 17, ...fonts.semibold },
});
